import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { subDays } from 'date-fns'
import i18next, { t } from 'i18next'
import { makeAutoObservable } from 'mobx'
import type { PaymentService } from 'src/service/Payment.service'

import type { Academy, Teacher, User } from '@/dto'
import type { B2gContract } from '@/dto/B2g/B2gContract'
import { UserPermission } from '@/models/(Permission)/UserPermission'
import { CustomRouterStore } from '@/router/CustomRouterStore'
import { envUtils } from '@/utils/envUtils'

class CommonRepo {
  teacherId?: Teacher['teacherId'] // 사용하지 않는 것으로 보이나 앱 등도 확인이 필요해서 일단 유지
  user?: User
  permission = new UserPermission({
    userAuthority: 'MANAGER_TEACHER',
    academyFeatureAuthority: 'BASIC',
    subscribedCurriculums: null,
    payStatus: '무료체험',
    isSchoolflat: false,
    isSeoltab: false,
    isFranchise: false,
  })
  academy?: Academy
  router = new CustomRouterStore()
  payment: PaymentService | null = null
  b2gContract: B2gContract | null = null
  version = '' // 안드로이드 등 디바이스 버전
  device = '' //  디바이스

  hasWorksheet = false

  constructor() {
    makeAutoObservable(this)
  }

  clear() {
    this.academy = this.teacherId = this.user = undefined
  }

  get isSolo() {
    return this.permission.bySubscription.isSolo
  }

  get isSchoolflat() {
    return (
      envUtils.isSchool ||
      !!(this.academy?.customerType && this.academy.customerType?.includes('B2G'))
    )
  }

  get isSeoltab() {
    return this.academy?.academyAdditionalData.isSeoltab ?? false
  }

  private get _studentContractPlans() {
    if (
      !this.isSchoolflat ||
      !this.academy ||
      !this.academy.isStudentContractPlan ||
      !this.academy.specialContractPlans
    )
      return null
    const { specialContractPlans } = this.academy
    return specialContractPlans.map((plan, index, plans) => {
      const nextPlan = plans[index + 1]
      return {
        ...plan,
        contractStartDate: new Date(plan.contractStartDate),
        contractEndDate: nextPlan
          ? subDays(new Date(nextPlan.contractStartDate), 1)
          : this.academy?.payEndDate
            ? new Date(this.academy.payEndDate)
            : null,
      }
    })
  }
  get currentStudentContractPlan() {
    return this._studentContractPlans?.[0]
  }
  get nextStudentContractPlans() {
    const plans = this._studentContractPlans?.slice(1)
    return plans?.length ? plans : null
  }
  // window.location.host랑 다른 값
  // ui상에서 사용되는 host (window.location.host가 mathflat.com이어도 ui상에서 schoolflat.com으로 보여야할 때 있음)
  get host() {
    if (this.isSchoolflat) {
      return `schoolflat.com`
    }
    return `${domainProvider.TARGET_DOMAIN}`
  }

  get appName() {
    if (this.isSchoolflat) {
      return 'schoolflat'
    }
    return 'mathflat'
  }

  get appKorName() {
    if (this.isSchoolflat) {
      return '스쿨플랫'
    }
    return '매쓰플랫'
  }

  get agencyNameInKor() {
    if (i18next.exists('학원')) {
      return t('학원')
    }
    // 언어팩 로드 실패 시 isSchoolflat 분기로 fallback
    return this.isSchoolflat ? '학교' : '학원'
  }

  get isFranchise() {
    return this.academy?.isFranchise ?? false
  }

  get isCreverse() {
    return this.academy?.isCreverse ?? false
  }

  // 현재 미사용
  get isElectronApp() {
    return window.bridge !== undefined
  }
}

const commonRepo = new CommonRepo()
export default commonRepo
